import { defineMessages, useIntl } from 'react-intl';
import type { FieldValues } from 'react-hook-form';
import type { FieldConfig } from '../field';
import type { FieldHookArgs } from './model/types';

const messages = defineMessages({
    email: {
        defaultMessage: 'Email Address',
        id: 'account.login.email-address',
    },
    password: {
        id: 'account.login.password',
        defaultMessage: 'Password',
    },
    fieldRequiredMessage: {
        id: 'account.login.error.required',
        defaultMessage: '{field} is required',
    },
    fieldInvalidMessage: {
        id: 'account.login.error.invalid',
        defaultMessage: 'Please enter a valid {field}',
    },
    rememberMe: {
        id: 'account.login.rememberMe',
        defaultMessage: 'Remember me',
    },
});

export function useLoginFields<T extends FieldValues = FieldValues>({
    form: {
        control,
        formState: { errors },
    },
    prefix = '',
}: FieldHookArgs<T>): Record<string, FieldConfig> {
    const intl = useIntl();

    const fields: Record<string, FieldConfig> = {
        email: {
            id: `${prefix ? `${prefix}-` : ''}email-address-input`,
            name: 'email',
            type: 'email',
            label: intl.formatMessage(messages.email),
            defaultValue: '',
            rules: {
                required: intl.formatMessage(messages.fieldRequiredMessage, {
                    field: 'Email address',
                }),
                pattern: {
                    value: /\S+@\S+\.[\S]{2,6}/,
                    message: intl.formatMessage(messages.fieldInvalidMessage, {
                        field: 'email address',
                    }),
                },
            },
            error: errors.email,
            control,
            variant: 'new-style',
        },
        password: {
            id: `${prefix ? `${prefix}-` : ''}password-input`,
            name: 'password',
            type: 'password',
            label: intl.formatMessage(messages.password),
            ariaLabel: 'Password',
            defaultValue: '',
            rules: {
                required: intl.formatMessage(messages.fieldRequiredMessage, {
                    field: 'Password',
                }),
            },
            error: errors.password,
            autoComplete: 'current-password',
            control,
            variant: 'new-style',
        },
        rememberMe: {
            id: `${prefix ? `${prefix}-` : ''}remember-me-button`,
            name: 'rememberMe',
            type: 'checkbox',
            label: intl.formatMessage(messages.rememberMe),
            ariaLabel: 'Remember me',
            defaultValue: false,
            error: errors.rememberMe,
            control,
            variant: 'new-style',
        },
    };

    return fields;
}
